import React, { useState, useEffect } from 'react';
import api, { documentAPI } from '../services/api';
import SearchableSelect from './SearchableSelect';
import Swal from 'sweetalert2';
import { generateLicensePlateReport } from '../utils/generateLicensePlateReport';
import { generateMultiSeriesReport } from '../utils/generateMultiSeriesReport';
import { useAuth } from '../context/AuthContext';

const SinglePlateInput = () => {
  const { user } = useAuth();
  const [provinces, setProvinces] = useState([]);
  const [plateRows, setPlateRows] = useState([{
    id: Date.now(),
    provinceId: '',
    series: '',
    letter: '',
    number: '',
    color: 'trắng',
    unitType: 'bộ',
    vehicleType: 'ô tô'
  }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProvinces();
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await api.get('/provinces');
      setProvinces(response.data);
      
      // Set Lâm Đồng mặc định cho dòng đầu tiên
      const lamDong = response.data.find(p => p.code === '49');
      if (lamDong && plateRows.length > 0) {
        setPlateRows(prev => prev.map((row, index) => 
          index === 0 ? { ...row, provinceId: lamDong.id, series: lamDong.code } : row
        ));
      }
    } catch (error) {
      console.error('Error fetching provinces:', error);
    }
  };

  const handleProvinceChange = (rowId, provinceId) => {
    const province = provinces.find(p => p.id === parseInt(provinceId));
    setPlateRows(prev => prev.map(row => 
      row.id === rowId 
        ? { ...row, provinceId, series: province ? province.code : '' }
        : row
    ));
  };

  const handleLetterChange = (rowId, value) => {
    const upperValue = value.toUpperCase();
    // Cho phép chữ cái (A-Z) và số (0-9), tối đa 2 ký tự
    if (upperValue.length <= 2 && /^[A-Z0-9]*$/.test(upperValue)) {
      setPlateRows(prev => prev.map(row => 
        row.id === rowId ? { ...row, letter: upperValue } : row
      ));
    }
  };

  const handleNumberChange = (rowId, value) => {
    if (value.length <= 5 && /^\d*$/.test(value)) {
      setPlateRows(prev => prev.map(row => 
        row.id === rowId ? { ...row, number: value } : row
      ));
    }
  };

  const handleFieldChange = (rowId, field, value) => {
    setPlateRows(prev => prev.map(row => {
      if (row.id === rowId) {
        const updatedRow = { ...row, [field]: value };
        
        // Nếu thay đổi vehicleType sang "mô tô", tự động set unitType = "biển"
        if (field === 'vehicleType' && value === 'mô tô') {
          updatedRow.unitType = 'biển';
        }
        // Nếu thay đổi vehicleType sang "ô tô", tự động set unitType = "bộ"
        else if (field === 'vehicleType' && value === 'ô tô') {
          updatedRow.unitType = 'bộ';
        }
        
        return updatedRow;
      }
      return row;
    }));
  };

  // Kiểm tra xem dòng cuối có đủ thông tin để thêm dòng mới không
  const isLastRowComplete = (row) => {
    return row.provinceId && row.letter && row.number.length === 5;
  };

  // Kiểm tra xem tất cả các dòng đã nhập đủ thông tin chưa
  const areAllRowsComplete = () => {
    return plateRows.every(row => isLastRowComplete(row));
  };

  const handleAddRow = () => {
    const lastRow = plateRows[plateRows.length - 1];
    setPlateRows(prev => [...prev, {
      id: Date.now(),
      provinceId: lastRow.provinceId,
      series: lastRow.series,
      letter: '',
      number: '',
      color: lastRow.color,
      unitType: lastRow.unitType,
      vehicleType: lastRow.vehicleType
    }]);
  };

  const handleRemoveRow = (rowId) => {
    if (plateRows.length > 1) {
      setPlateRows(prev => prev.filter(row => row.id !== rowId));
    }
  };

  const handleSubmitAll = async () => {
    // Validate tất cả các dòng
    const invalidRows = plateRows.filter(row => 
      !row.provinceId || !row.letter || row.number.length !== 5
    );

    if (invalidRows.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng nhập đầy đủ thông tin cho tất cả các biển số',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    try {
      setLoading(true);
      
      const results = {
        success: [],
        successPlates: [], // Lưu thông tin đầy đủ để tạo báo cáo
        failed: []
      };
      
      // Tạo từng biển số và thu thập kết quả
      for (const row of plateRows) {
        const formattedNumber = `${row.number.slice(0, 3)}.${row.number.slice(3)}`;
        const fullPlate = `${row.series}${row.letter}-${formattedNumber}`;
        
        const payload = {
          provinceId: row.provinceId,
          series: `${row.series}${row.letter}`,
          number: formattedNumber,
          color: row.color,
          unitType: row.unitType,
          vehicleType: row.vehicleType
        };

        try {
          const response = await api.post('/license-plates/single', payload);
          results.success.push(fullPlate);
          results.successPlates.push({
            ...response.data,
            fullPlate,
            provinceCode: row.series,
            series: row.letter,
            color: row.color,
            unitType: row.unitType
          });
        } catch (error) {
          results.failed.push({
            plate: fullPlate,
            reason: error.response?.data?.message || 'Lỗi không xác định'
          });
        }
      }

      // Hiển thị kết quả
      if (results.failed.length === 0 && results.success.length > 0) {
        // Tất cả thành công - Hỏi có muốn tải báo cáo
        const result = await Swal.fire({
          icon: 'success',
          title: 'Thành công!',
          text: `Đã tạo thành công ${results.success.length} biển số`,
          showCancelButton: true,
          confirmButtonText: '📄 Tải báo cáo',
          cancelButtonText: 'Đóng',
          confirmButtonColor: '#4caf50',
          cancelButtonColor: '#6b7280'
        });

        if (result.isConfirmed && user && user.unit) {
          // Group plates theo seri + color + unitType
          const platesBySeries = {};
          results.successPlates.forEach(plate => {
            // Key bao gồm: provinceCode + series + color + unitType
            const seriesKey = `${plate.provinceCode}${plate.series}_${plate.color}_${plate.unitType}`;
            if (!platesBySeries[seriesKey]) {
              platesBySeries[seriesKey] = {
                provinceCode: plate.provinceCode,
                series: plate.series,
                plates: [],
                color: plate.color,
                unitType: plate.unitType
              };
            }
            platesBySeries[seriesKey].plates.push(plate);
          });

          const seriesGroups = Object.values(platesBySeries);

          // Generate 1 báo cáo duy nhất chứa tất cả các seri
          try {
            const { saveAs } = await import('file-saver');
            
            const reportData = await generateMultiSeriesReport({
              unitName: user.unit.name,
              seriesGroups: seriesGroups
            });

            // 1. Tải file về máy
            saveAs(reportData.blob, reportData.filename);

            // 2. Upload lên server
            const formData = new FormData();
            formData.append('document', reportData.blob, reportData.filename);
            formData.append('title', reportData.filename.replace('.docx', ''));

            await documentAPI.uploadDocument(formData);

            Swal.fire({
              icon: 'success',
              title: 'Đã tải báo cáo!',
              text: `Báo cáo chứa ${seriesGroups.length} seri khác nhau đã được tải về và lưu vào Quản lý tài liệu`,
              confirmButtonColor: '#4caf50'
            });
          } catch (error) {
            console.error('Error generating/uploading report:', error);
            Swal.fire({
              icon: 'error',
              title: 'Lỗi!',
              text: 'Không thể tạo hoặc upload báo cáo',
              confirmButtonColor: '#1976d2'
            });
          }
        }
        
        // Reset về 1 dòng mới
        const lastRow = plateRows[plateRows.length - 1];
        setPlateRows([{
          id: Date.now(),
          provinceId: lastRow.provinceId,
          series: lastRow.series,
          letter: '',
          number: '',
          color: lastRow.color,
          unitType: lastRow.unitType,
          vehicleType: lastRow.vehicleType
        }]);
      } else if (results.success.length === 0) {
        // Tất cả thất bại
        const failedList = results.failed.map(f => `${f.plate}: ${f.reason}`).join('<br>');
        Swal.fire({
          icon: 'error',
          title: 'Không thể tạo biển số',
          html: `<div style="text-align: left; max-height: 300px; overflow-y: auto;">${failedList}</div>`,
          confirmButtonColor: '#1976d2'
        });
      } else {
        // Một số thành công, một số thất bại
        const failedList = results.failed.map(f => `${f.plate}: ${f.reason}`).join('<br>');
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Hoàn thành một phần',
          html: `
            <div style="text-align: left;">
              <p><strong>✅ Thành công: ${results.success.length} biển số</strong></p>
              <p><strong>❌ Thất bại: ${results.failed.length} biển số</strong></p>
              <div style="max-height: 200px; overflow-y: auto; margin-top: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px;">
                ${failedList}
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: '📄 Tải báo cáo',
          cancelButtonText: 'Đóng',
          confirmButtonColor: '#4caf50',
          cancelButtonColor: '#6b7280'
        });

        if (result.isConfirmed && user && user.unit && results.successPlates.length > 0) {
          // Group plates theo seri + color + unitType (có thể có nhiều seri khác nhau)
          const platesBySeries = {};
          results.successPlates.forEach(plate => {
            // Key bao gồm: provinceCode + series + color + unitType
            const seriesKey = `${plate.provinceCode}${plate.series}_${plate.color}_${plate.unitType}`;
            if (!platesBySeries[seriesKey]) {
              platesBySeries[seriesKey] = {
                provinceCode: plate.provinceCode,
                series: plate.series,
                plates: [],
                color: plate.color,
                unitType: plate.unitType
              };
            }
            platesBySeries[seriesKey].plates.push(plate);
          });

          const seriesGroups = Object.values(platesBySeries);

          // Generate báo cáo cho các biển thành công
          try {
            const { saveAs } = await import('file-saver');
            
            const reportData = await generateMultiSeriesReport({
              unitName: user.unit.name,
              seriesGroups: seriesGroups
            });

            // 1. Tải file về máy
            saveAs(reportData.blob, reportData.filename);

            // 2. Upload lên server
            const formData = new FormData();
            formData.append('document', reportData.blob, reportData.filename);
            formData.append('title', reportData.filename.replace('.docx', ''));

            await documentAPI.uploadDocument(formData);

            Swal.fire({
              icon: 'success',
              title: 'Đã tải báo cáo!',
              text: `Báo cáo chứa ${seriesGroups.length} seri đã được tải về và lưu vào Quản lý tài liệu`,
              confirmButtonColor: '#4caf50'
            });
          } catch (error) {
            console.error('Error generating/uploading report:', error);
            Swal.fire({
              icon: 'error',
              title: 'Lỗi!',
              text: 'Không thể tạo hoặc upload báo cáo',
              confirmButtonColor: '#1976d2'
            });
          }
        }
        
        // Xóa các dòng đã tạo thành công, giữ lại các dòng thất bại
        const successPlates = results.success.map(p => p.replace(/\./g, ''));
        setPlateRows(prev => prev.filter(row => {
          const rowPlate = `${row.series}${row.letter}-${row.number.slice(0, 3)}${row.number.slice(3)}`;
          return !successPlates.includes(rowPlate);
        }));
      }
    } catch (error) {
      console.error('Error creating plates:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Lỗi không xác định khi tạo biển số',
        confirmButtonColor: '#1976d2'
      });
    } finally {
      setLoading(false);
    }
  };

  // Chuyển đổi provinces thành options cho SearchableSelect
  const provinceOptions = provinces.map(p => ({
    value: p.id,
    label: `${p.code} - ${p.name}`
  }));

  return (
    <div className="single-plate-input">
      <div className="plate-rows-container">
        <div className="plate-row-header">
          <div className="header-cell">Tỉnh/TP</div>
          <div className="header-cell">Loại xe</div>
          <div className="header-cell">Seri</div>
          <div className="header-cell">Số biển</div>
          <div className="header-cell">Màu sắc</div>
          <div className="header-cell">Đơn vị</div>
          <div className="header-cell">Thao tác</div>
        </div>

        {plateRows.map((row, index) => (
          <div key={row.id} className="plate-row">
            <div className="row-cell province-cell">
              <SearchableSelect
                options={provinceOptions}
                value={row.provinceId}
                onChange={(value) => handleProvinceChange(row.id, value)}
                placeholder="Chọn tỉnh/TP"
              />
            </div>

            <div className="row-cell">
              <select
                value={row.vehicleType}
                onChange={(e) => handleFieldChange(row.id, 'vehicleType', e.target.value)}
                required
              >
                <option value="ô tô">Ô tô</option>
                <option value="mô tô">Mô tô</option>
                <option value="rơ moóc">Rơ moóc</option>
              </select>
            </div>

            <div className="row-cell series-cell">
              <input
                type="text"
                value={row.series}
                disabled
                className="series-code-inline"
              />
              <input
                type="text"
                value={row.letter}
                onChange={(e) => handleLetterChange(row.id, e.target.value)}
                placeholder="AB/A1"
                maxLength="2"
                className="series-letter-inline"
                required
              />
            </div>

            <div className="row-cell">
              <input
                type="text"
                value={row.number}
                onChange={(e) => handleNumberChange(row.id, e.target.value)}
                placeholder="00001"
                maxLength="5"
                required
              />
            </div>

            <div className="row-cell">
              <select
                value={row.color}
                onChange={(e) => handleFieldChange(row.id, 'color', e.target.value)}
                required
              >
                <option value="trắng">Trắng</option>
                <option value="vàng">Vàng</option>
                <option value="xanh">Xanh</option>
              </select>
            </div>

            <div className="row-cell">
              <select
                value={row.unitType}
                onChange={(e) => handleFieldChange(row.id, 'unitType', e.target.value)}
                required
              >
                {row.vehicleType === 'mô tô' ? (
                  // Mô tô: chỉ có "Biển"
                  <option value="biển">Biển</option>
                ) : (
                  // Ô tô/Rơ moóc: có các options khác (không có "biển")
                  <>
                    <option value="bộ">Bộ</option>
                    <option value="bộ dài">Bộ dài</option>
                    <option value="bộ ngắn">Bộ ngắn</option>
                    <option value="biển dài">Biển dài</option>
                    <option value="biển ngắn">Biển ngắn</option>
                  </>
                )}
              </select>
            </div>

            <div className="row-cell actions-cell">
              {index === plateRows.length - 1 ? (
                // Dòng cuối: hiển thị cả 2 nút
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button
                    type="button"
                    onClick={handleAddRow}
                    disabled={!isLastRowComplete(row)}
                    className="btn-add-row"
                    title={isLastRowComplete(row) ? "Thêm dòng" : "Vui lòng nhập đủ thông tin"}
                  >
                    +
                  </button>
                  {plateRows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(row.id)}
                      className="btn-remove-row"
                      title="Xóa dòng này"
                    >
                      ×
                    </button>
                  )}
                </div>
              ) : (
                // Các dòng khác: chỉ hiển thị nút xóa
                <button
                  type="button"
                  onClick={() => handleRemoveRow(row.id)}
                  className="btn-remove-row"
                  title="Xóa dòng"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="form-actions-bottom">
        <button
          type="button"
          onClick={handleSubmitAll}
          disabled={loading || !areAllRowsComplete()}
          className="btn-submit"
          title={areAllRowsComplete() ? `Tạo ${plateRows.length} biển số` : "Vui lòng nhập đủ thông tin cho tất cả các dòng"}
        >
          {loading ? 'Đang xử lý...' : `Tạo ${plateRows.length} biển số`}
        </button>
      </div>
    </div>
  );
};

export default SinglePlateInput;
